import {
  Stack,
  Box,
  Snackbar,
  Alert,
  Typography,
  Button,
  TextField,
  MenuItem,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import SubmitLockButton from 'components/submit-lock-button/submit-lock-button.component'
import PrintIcon from '@mui/icons-material/Print'
import { DateTime } from 'luxon'

import InvoiceTable from '../invoice-details/invoice-table/invoice-table'
import {
  TransformInvoiceResponse,
  InvoiceType,
  InvoiceResponse,
  BatchResponse,
  CreateBatchPayload,
  isToBeInvoicedWorkspaceTab,
} from 'store/invoices/invoices.types'
import { exportInvoicesToCSV } from '../../../utils/csv-export'
import {
  useCreateBatchMutation,
  useLazyGetBatchByIdQuery,
  useLazyGetInvoiceByIdQuery,
} from 'store/invoices/invoices.api'
import { useSelector } from 'react-redux'
import { RootState } from 'store'
import BatchInvoiceEmailDialog from './batch-invoice-email-dialog'
import { getInvoiceAgingDays, matchesInvoiceAgingFilter } from '../invoice.utils'
import { listTableToolbarSx } from 'components/list-table/list-table.styles'

function invoiceRowMatchesSearch(invoice: InvoiceResponse, query: string): boolean {
  if (!query) return true
  const q = query.toLowerCase().trim()
  const customerName = (invoice.customerName ?? '').toLowerCase()
  const orderId = (invoice.orderId ?? '').toLowerCase()
  const userOrderId = (invoice.userOrderId ?? '').toLowerCase()
  const loadNumber = (invoice.loadNumber ?? '').toLowerCase()
  const userInvoiceId = (invoice.userInvoiceId ?? '').toLowerCase()
  const amountStr = String(invoice.totalAmount)
  const agingDays = getInvoiceAgingDays(invoice)
  const agingStr = `${agingDays} days`
  return (
    customerName.includes(q) ||
    orderId.includes(q) ||
    userOrderId.includes(q) ||
    loadNumber.includes(q) ||
    userInvoiceId.includes(q) ||
    amountStr.includes(q) ||
    agingStr.includes(q) ||
    String(agingDays).includes(q)
  )
}

const ALL_FILTER_VALUE = 'ALL'

const paidTabSelectSx = {
  minWidth: 108,
  '& .MuiOutlinedInput-root': {
    bgcolor: 'primary.main',
    color: 'primary.contrastText',
    borderRadius: 1,
    fontWeight: 600,
    '& fieldset': { border: 'none' },
    '&:hover': { bgcolor: 'primary.dark' },
    '&.Mui-focused': { bgcolor: 'primary.main' },
    '&.Mui-focused fieldset': { border: 'none' },
  },
  '& .MuiSelect-select': {
    py: 0.75,
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiSelect-icon': { color: 'common.white' },
} as const

function getInvoiceDateForFilter(invoice: InvoiceResponse): DateTime | null {
  if (invoice.issueDate) {
    const issueDate = DateTime.fromISO(invoice.issueDate)
    if (issueDate.isValid) return issueDate
  }

  if (invoice.createdDate) {
    const createdDate = DateTime.fromISO(invoice.createdDate)
    if (createdDate.isValid) return createdDate
  }

  return null
}

type InvoiceDefaultViewProps = {
  invoices: TransformInvoiceResponse[]
  invoiceType: InvoiceType
}

const InvoiceDefaultView = ({ invoices, invoiceType }: InvoiceDefaultViewProps) => {
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<Set<string>>(new Set())
  const [errorOpen, setErrorOpen] = useState(false)
  const [errorMessage, setErrorMessage] = useState('Please select at least one invoice to export.')
  const [batchDialogOpen, setBatchDialogOpen] = useState(false)
  const [createdBatch, setCreatedBatch] = useState<BatchResponse | null>(null)
  const [batchDialogInvoices, setBatchDialogInvoices] = useState<InvoiceResponse[]>([])
  const [tableSearch, setTableSearch] = useState('')
  const [selectedYear, setSelectedYear] = useState<string>(ALL_FILTER_VALUE)
  const [selectedMonth, setSelectedMonth] = useState<string>(ALL_FILTER_VALUE)
  const [groupByBatch, setGroupByBatch] = useState(false)
  const { selectedCustomerId, duration } = useSelector((state: RootState) => state.invoices)
  const [createBatch, { isLoading: isCreateBatchLoading }] = useCreateBatchMutation()
  const [getBatchById] = useLazyGetBatchByIdQuery()
  const [getInvoiceById] = useLazyGetInvoiceByIdQuery()
  const isPaidTab = invoiceType === 'PAID'

  useEffect(() => {
    if (!isPaidTab) {
      setSelectedYear(ALL_FILTER_VALUE)
      setSelectedMonth(ALL_FILTER_VALUE)
    }
  }, [isPaidTab])

  const availableYears = useMemo(() => {
    const years = new Set<number>()
    invoices.forEach((group) => {
      group.invoiceData.forEach((invoice) => {
        const date = getInvoiceDateForFilter(invoice)
        if (date) years.add(date.year)
      })
    })
    years.add(DateTime.now().year)
    return Array.from(years).sort((a, b) => b - a)
  }, [invoices])

  const monthOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_, index) => {
        const month = index + 1
        return {
          value: String(month),
          label: DateTime.fromObject({ month }).toFormat('LLLL'),
        }
      }),
    []
  )

  const filteredInvoices = useMemo(() => {
    const query = tableSearch.trim().toLowerCase()
    let effectiveYear: number | null = null
    let effectiveMonth: number | null = null

    if (isPaidTab) {
      const hasMonthFilter = selectedMonth !== ALL_FILTER_VALUE
      effectiveYear =
        selectedYear === ALL_FILTER_VALUE
          ? hasMonthFilter
            ? DateTime.now().year
            : null
          : Number(selectedYear)
      effectiveMonth = hasMonthFilter ? Number(selectedMonth) : null
    }

    return invoices
      .map((group) => ({
        ...group,
        invoiceData: group.invoiceData.filter((inv) => {
          const matchesSearch = invoiceRowMatchesSearch(inv, query)
          if (!matchesSearch) return false
          if (!effectiveYear && !effectiveMonth) return true

          const invoiceDate = getInvoiceDateForFilter(inv)
          if (!invoiceDate) return false

          const matchesYear = effectiveYear ? invoiceDate.year === effectiveYear : true
          const matchesMonth = effectiveMonth ? invoiceDate.month === effectiveMonth : true
          return matchesYear && matchesMonth
        }),
      }))
      .filter((group) => group.invoiceData.length > 0)
  }, [invoices, tableSearch, selectedYear, selectedMonth, isPaidTab])

  const visibleInvoices = useMemo(() => {
    return filteredInvoices.flatMap((group) =>
      group.invoiceData.filter((invoice) =>
        matchesInvoiceAgingFilter(invoice, invoiceType, duration)
      )
    )
  }, [filteredInvoices, invoiceType, duration])

  const totalInvoiceAmount = useMemo(() => {
    const invoicesToSum =
      selectedInvoiceIds.size > 0
        ? visibleInvoices.filter((invoice) => selectedInvoiceIds.has(invoice.id))
        : visibleInvoices

    return invoicesToSum.reduce((acc, invoice) => acc + invoice.totalAmount, 0)
  }, [visibleInvoices, selectedInvoiceIds])

  const currency = filteredInvoices[0]?.invoiceData[0]?.currency || invoices[0]?.invoiceData[0]?.currency || 'CAD'

  const hasCreatedBatch = useMemo(
    () =>
      invoices.some((group) =>
        group.invoiceData.some((invoice) => Boolean(invoice.batchId))
      ),
    [invoices]
  )

  const showCreateBatchButton =
    isToBeInvoicedWorkspaceTab(invoiceType) && Boolean(selectedCustomerId)

  useEffect(() => {
    if (!hasCreatedBatch) {
      setGroupByBatch(false)
    }
  }, [hasCreatedBatch])

  const handleExportCSV = () => {
    if (selectedInvoiceIds.size === 0) {
      setErrorMessage('Please select at least one invoice to export.')
      setErrorOpen(true)
      return
    }
    exportInvoicesToCSV(invoices, selectedInvoiceIds)
  }

  const handleCreateBatchInvoice = async () => {
    if (selectedInvoiceIds.size === 0) {
      setErrorMessage('Please select at least one invoice to create a batch.')
      setErrorOpen(true)
      return
    }

    try {
      const selectedIds = Array.from(selectedInvoiceIds)
      const selectedInvoices = invoices.flatMap((group) =>
        group.invoiceData.filter((invoice) => selectedIds.includes(invoice.id))
      )

      const totalAmount = selectedInvoices.reduce(
        (acc, invoice) => acc + invoice.totalAmount,
        0
      )

      const payload: CreateBatchPayload = {
        invoiceIds: selectedIds,
        totalAmount,
      }

      const batch = await createBatch(payload).unwrap()

      setCreatedBatch(batch)
      setBatchDialogInvoices(selectedInvoices)
      setBatchDialogOpen(true)
    } catch (error) {
      setErrorMessage('Failed to create batch invoice. Please try again.')
      setErrorOpen(true)
    }
  }

  const handleOpenBatchDialogFromTable = async (batchId: number) => {
    try {
      const batch = await getBatchById({ batchId }).unwrap()
      if (!batch.invoiceIds?.length) {
        setErrorMessage('Could not find invoices for this batch.')
        setErrorOpen(true)
        return
      }
      const invoicesInBatch = await Promise.all(
        batch.invoiceIds.map((invoiceId) => getInvoiceById({ id: invoiceId }).unwrap())
      )
      setCreatedBatch(batch)
      setBatchDialogInvoices(invoicesInBatch)
      setBatchDialogOpen(true)
    } catch {
      setErrorMessage('Failed to open batch email dialog. Please try again.')
      setErrorOpen(true)
    }
  }

  const handleCloseError = () => {
    setErrorOpen(false)
  }

  return (
    <Stack
      sx={{
        flex: 1,
        minHeight: 0,
        maxHeight: '100%',
        overflow: 'hidden',
        justifyContent: 'space-between',
      }}
    >
      <Stack spacing={2} sx={listTableToolbarSx}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.5,
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              {isPaidTab && (
                <>
                  <TextField
                    select
                    size="small"
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                    sx={paidTabSelectSx}
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (value) =>
                        value === ALL_FILTER_VALUE ? 'Year' : String(value),
                    }}
                  >
                    <MenuItem value={ALL_FILTER_VALUE}>All years</MenuItem>
                    {availableYears.map((year) => (
                      <MenuItem key={year} value={String(year)}>
                        {year}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    size="small"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    sx={{ ...paidTabSelectSx, minWidth: 128 }}
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (value) => {
                        if (value === ALL_FILTER_VALUE) return 'Month'
                        const m = monthOptions.find((mo) => mo.value === value)
                        return m?.label ?? 'Month'
                      },
                    }}
                  >
                    <MenuItem value={ALL_FILTER_VALUE}>All months</MenuItem>
                    {monthOptions.map((month) => (
                      <MenuItem key={month.value} value={month.value}>
                        {month.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </>
              )}
              {!isPaidTab && hasCreatedBatch && (
                <>
                  <Button
                    disableElevation
                    variant="contained"
                    onClick={() => setGroupByBatch(false)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 1,
                      minWidth: 118,
                      ...(groupByBatch
                        ? {
                            bgcolor: 'grey.500',
                            color: 'common.white',
                            '&:hover': { bgcolor: 'grey.600' },
                          }
                        : {
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            '&:hover': { bgcolor: 'primary.dark' },
                          }),
                    }}
                  >
                    By Invoice#
                  </Button>
                  <Button
                    disableElevation
                    variant="contained"
                    onClick={() => setGroupByBatch(true)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 1,
                      minWidth: 118,
                      ...(!groupByBatch
                        ? {
                            bgcolor: 'grey.500',
                            color: 'common.white',
                            '&:hover': { bgcolor: 'grey.600' },
                          }
                        : {
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            '&:hover': { bgcolor: 'primary.dark' },
                          }),
                    }}
                  >
                    By Batch
                  </Button>
                </>
              )}
            </Stack>
            <TextField
              placeholder="Search"
              variant="outlined"
              size="small"
              value={tableSearch}
              onChange={(e) => setTableSearch(e.target.value)}
              sx={{ minWidth: 200, flexShrink: 0 }}
            />
        </Box>
      </Stack>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <InvoiceTable
          invoices={filteredInvoices}
          onSelectedInvoicesChange={setSelectedInvoiceIds}
          invoiceType={invoiceType}
          showBatchColumn={Boolean(selectedCustomerId) && hasCreatedBatch}
          onBatchClick={handleOpenBatchDialogFromTable}
          groupByBatch={groupByBatch}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          flexShrink: 0,
        }}
      >
        {showCreateBatchButton ? (
          <SubmitLockButton
            variant="contained"
            onClick={handleCreateBatchInvoice}
            disabled={isCreateBatchLoading}
          >
            Create Batch Invoice
          </SubmitLockButton>
        ) : (
          <Box />
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            Total Invoice Amount: {currency} {totalInvoiceAmount.toFixed(2)}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handleExportCSV}
          >
            Export
          </Button>
        </Box>
      </Box>
      <Snackbar
        open={errorOpen}
        autoHideDuration={4000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
      {createdBatch && (
        <BatchInvoiceEmailDialog
          open={batchDialogOpen}
          batch={createdBatch}
          selectedInvoices={batchDialogInvoices}
          handleClose={() => setBatchDialogOpen(false)}
        />
      )}
    </Stack>
  )
}

export default InvoiceDefaultView
